import type { SupabaseClient } from '@supabase/supabase-js';

import { logger } from '@/utils/logger';

/**
 * BI-5-03 client-side delivery for server-resolved franchise lock context.
 *
 * Owner-only RLS on `brand_kits` hides parent-kit rows from the browser, so
 * this is the ONLY client-side path to parent-kit lock context — it wraps
 * the `get_effective_locked_fields()` SECURITY DEFINER RPC (BI-5-03 migration).
 *
 * PHASE-PINNED SEMANTIC (BI-5-01, binding across the whole phase):
 *   - `lockedFields`          = DISPLAY-ONLY union of own + ancestor locks
 *                                (e.g. lock badges on a kit's own settings
 *                                screen). NEVER an enforcement input.
 *   - `inheritedLockedFields` = THE enforcement set (ancestor locks ONLY).
 *                                Every guard in this phase (PostService,
 *                                QuickPostService, BrandKitService,
 *                                Media Studio affordances) reads ONLY this.
 *
 * Client fail-open is deliberate: an RPC hiccup must never brick a consumer
 * save. The trigger (BI-5-03 migration) and BI-5-02's server routes remain
 * the authoritative stops regardless of what this client cache returns.
 */
export interface EffectiveLockedFieldsClientResult {
  lockedFields: string[];
  inheritedLockedFields: string[];
  lockedValues: Record<string, string>;
}

const EMPTY_RESULT: EffectiveLockedFieldsClientResult = {
  lockedFields: [],
  inheritedLockedFields: [],
  lockedValues: {},
};

const CACHE_TTL_MS = 30_000;

interface CacheEntry {
  value: EffectiveLockedFieldsClientResult;
  expiresAt: number;
}

let cacheEntry: CacheEntry | null = null;
let inflight: Promise<EffectiveLockedFieldsClientResult> | null = null;

interface RpcShape {
  locked_fields?: string[] | null;
  inherited_locked_fields?: string[] | null;
  locked_values?: Record<string, string> | null;
}

function normalizeRpcResult(raw: unknown): EffectiveLockedFieldsClientResult {
  const shape = (raw ?? {}) as RpcShape;
  return {
    lockedFields: Array.isArray(shape.locked_fields) ? shape.locked_fields : [],
    inheritedLockedFields: Array.isArray(shape.inherited_locked_fields)
      ? shape.inherited_locked_fields
      : [],
    lockedValues:
      shape.locked_values && typeof shape.locked_values === 'object' ? shape.locked_values : {},
  };
}

/**
 * Resolve the calling account's effective locked-field context, cached
 * module-wide for ~30s. RPC failure or a missing function (e.g. against a
 * stale local schema) fails OPEN — returns empty locks and logs the error —
 * because this is a UX layer; the DB trigger + BI-5-02 server routes are the
 * hard stops and must never be bypassed by a client cache miss either way.
 */
export async function getEffectiveLockedFields(
  client: SupabaseClient,
): Promise<EffectiveLockedFieldsClientResult> {
  const now = Date.now();
  if (cacheEntry && cacheEntry.expiresAt > now) {
    return cacheEntry.value;
  }
  if (inflight) {
    return inflight;
  }

  inflight = (async () => {
    try {
      const { data, error } = await client.rpc('get_effective_locked_fields');
      if (error) {
        logger.error('[lockedFieldsClient] get_effective_locked_fields RPC failed', error);
        return EMPTY_RESULT;
      }
      const result = normalizeRpcResult(data);
      cacheEntry = { value: result, expiresAt: Date.now() + CACHE_TTL_MS };
      return result;
    } catch (err) {
      logger.error('[lockedFieldsClient] get_effective_locked_fields threw', err);
      return EMPTY_RESULT;
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

/** Invalidate the module-level cache — call on any kit-change event (e.g.
 * wired into useBrandKitCacheInvalidator alongside its ['brand-kits'] bust). */
export function invalidateLockedFieldsCache(): void {
  cacheEntry = null;
}
